"use client";

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { createComment, logActivity } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { Loader2 } from '@/components/icons';
import { Send } from 'lucide-react';
import EmojiPicker from './EmojiPicker';

interface CommentFormProps {
  noteId: string;
  session: Session | null;
  onCommentAdded: () => void;
  noteAuthorId: string; // Nuevo prop: ID del autor de la nota
  noteAuthorUsername: string; // Nuevo prop: Username del autor de la nota
  autoFocus?: boolean;
  parentId?: string; // Nuevo prop para respuestas
  placeholder?: string; // Opcional
}
const CommentForm: React.FC<CommentFormProps> = ({ noteId, session, onCommentAdded, noteAuthorId, noteAuthorUsername, autoFocus, parentId, placeholder = "Escribí algo..." }) => {
  const [commentContent, setCommentContent] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const submitRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (autoFocus && textareaRef.current) {
      try { textareaRef.current.focus(); textareaRef.current.selectionStart = textareaRef.current.value.length; } catch (e) {}
    }
  }, [autoFocus]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!session?.user?.id) {
      console.error('CommentForm: No session user ID');
      toast({
        title: "Error",
        description: "Debés iniciar sesión para comentar.",
        variant: "destructive",
      });
      return;
    }
    if (!commentContent.trim()) {
      console.warn('CommentForm: Comment content is empty');
      toast({
        title: "Error",
        description: "El comentario no puede estar vacío.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await createComment(noteId, session.user.id, commentContent, parentId);
      
      const contentForActivity = commentContent; // Guardar antes de limpiar
      setCommentContent('');
      onCommentAdded();

      // Determinar el mensaje de actividad
      const activityType = parentId ? 'replied_to_comment' : 'commented_on_note';

      // Registrar la actividad
      await logActivity(
        session.user.id,
        activityType,
        contentForActivity,
        `/notas/${noteId}`,
        noteAuthorId
      );

    } catch (error) {
      console.error("Error creating comment:", error);
      toast({
        title: "Error al comentar",
        description: "No se pudo añadir el comentario. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0 mt-3">
      <div className="flex gap-2 items-center">
        <Textarea
          placeholder={placeholder}
          value={commentContent}
          onChange={(e) => setCommentContent(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || !session}
          rows={1}
          aria-label="Escribe tu comentario"
          tabIndex={0}
          ref={textareaRef}
          className="resize-none flex-1 min-h-[40px]"
        />
        {/* Emoji picker button */}
        <EmojiPicker
          onEmojiSelect={(emoji) => setCommentContent(commentContent + emoji)}
          disabled={loading || !session}
        />
        <Button
          type="submit"
          disabled={loading || !session || !commentContent.trim()}
          size="icon"
          className="flex-shrink-0 h-10 w-10 border-0"
          aria-label="Publicar comentario"
          tabIndex={0}
          ref={submitRef}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </form>
  );
};

export default CommentForm;